import type { UserAvatarColor } from 'src/models/user.models'

/**
 * Frozen demo data for the card-detail Activity tab (EX-40600).
 *
 * EVERY RECORD HERE IS FABRICATED — deliberately, as a demo surface until the
 * Activity read endpoint is callable. See `useAccountActivityData` for how the
 * audience is actually determined.
 *
 * The shapes below are NOT invented: they are the frozen public contract from
 * extend-api `docs/card-management/activity-api-contract.md` (EX-40583,
 * PR #9999), field-for-field. `GET /creditcards/{creditCardId}/cardmanagement/
 * activity?limit=&before=` returns exactly `ActivityPage`. Keeping the demo on
 * the real shape is the point: wiring the endpoint replaces the fetch inside
 * `useAccountActivityData` and touches nothing else, with no rename sweep
 * through the components.
 *
 * Contract details worth preserving as you edit:
 *
 * - `message` and `detail` are SERVER-AUTHORED display strings. Clients must
 *   not reconstruct them from internal actions or metadata — which is also why
 *   they are not run through `translate` at the render site. Localization of
 *   activity text is the backend's job, not ours.
 * - `occurredAt` is Unix epoch MILLISECONDS, not an ISO string. That differs
 *   from the Statements tab (ISO-8601) because the contract says so; do not
 *   "fix" it for consistency.
 * - `avatarUrl`, `detail`, and `nextCursor` are always-present JSON fields that
 *   are explicitly `null` when absent, so they are typed nullable rather than
 *   optional.
 * - Pagination is CURSOR-based, newest-first. Cursors are opaque: never parse,
 *   construct, persist, or reuse one across cards.
 *
 * Two contract facts the design does not yet reflect, both intentional:
 *
 * 1. The initial ledger exposes only `actor.type = 'USER'`. The design also
 *    calls for Extend `SYSTEM` events (the Extend-logo avatar row). `SYSTEM` is
 *    in the union here because the design requires it and the contract names it
 *    as the future addition, and the row renders it correctly today — but the
 *    real endpoint will not return one until a source record, migration, and
 *    explicit contract change land.
 * 2. `outcome` is only `SUCCEEDED` | `FAILED`, while the design's tone dot has
 *    four tones. The dot is deliberately not built (see `ActivityEventRow`), so
 *    nothing here has to reconcile the two.
 */

/** Only `USER` is returned today; `SYSTEM` is the contract's named future case. */
export type ActivityActorType = 'USER' | 'SYSTEM'

/** Terminal public vocabulary. Deliberately not mapped to a colour today. */
export type ActivityOutcome = 'SUCCEEDED' | 'FAILED'

export interface ActivityActor {
  type: ActivityActorType
  /** Always present. */
  displayName: string
  /** Rendered when non-null; otherwise `initials` is shown. */
  avatarUrl: string | null
  /** Persisted user fallback color; absent for legacy/system actors. */
  avatarColor?: UserAvatarColor | null
  /** Always present, so the avatar always has a fallback. */
  initials: string
}

export interface ActivityEvent {
  id: string
  actor: ActivityActor
  /** Server-authored verb phrase, e.g. "raised the card limit". */
  message: string
  /** Server-authored qualifier, e.g. "+$3,000.00 - permanent". */
  detail: string | null
  outcome: ActivityOutcome
  /** Unix epoch MILLISECONDS. */
  occurredAt: number
}

export interface ActivityPage {
  /**
   * Echoed by the server so a client can reject or diagnose a response cached
   * under the wrong card. `useAccountActivityData` does exactly that.
   */
  creditCardId: string
  /** Newest first. */
  items: ActivityEvent[]
  /** `null` means this page is terminal. */
  nextCursor: string | null
}

/**
 * Opaque to every consumer — these are demo stand-ins for server cursors and
 * carry no meaning. Nothing outside this file may PARSE or CONSTRUCT one, or
 * persist it as domain data. Taking the `nextCursor` off a page and handing it
 * straight back is the intended use of an opaque token, not a violation of it.
 */
const DEMO_CURSOR_PAGE_2 = 'opaque_demo_cursor_page_2'
const DEMO_CURSOR_PAGE_3 = 'opaque_demo_cursor_page_3'

/**
 * The demo clock. Timestamps are anchored to module load rather than to fixed
 * calendar dates so the relative labels ("2 days ago") stay truthful however
 * long the endpoint takes to land — a frozen date would drift into "8 months
 * ago" and read as a stale feed rather than a demo.
 */
const now = Date.now()
const daysAgo = (days: number): number => now - days * 24 * 60 * 60 * 1000

const user = (
  displayName: string,
  initials: string,
  avatarUrl: string | null = null,
): ActivityActor => ({ type: 'USER', displayName, avatarUrl, initials })

const extend = (): ActivityActor => ({
  type: 'SYSTEM',
  displayName: 'Extend',
  avatarUrl: null,
  initials: 'E',
})

/**
 * Page 1 — the seeded feed from the design's `activity-seeded-feed` capture,
 * in the design's order.
 */
const DEMO_PAGE_1: ActivityEvent[] = [
  {
    id: 'pcmact_demo_0001',
    actor: user('Admin', 'AD'),
    message: 'raised the card limit',
    detail: '+$3,000.00 · permanent',
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(2),
  },
  {
    id: 'pcmact_demo_0002',
    actor: user('Jia Chen', 'JC'),
    message: 'revealed the card number',
    detail: null,
    outcome: 'SUCCEEDED',
    // Later the same relative day as the row above, so newest-first ordering is
    // exercised by rows that share a rendered label.
    occurredAt: daysAgo(2.4),
  },
  {
    id: 'pcmact_demo_0003',
    actor: user('Admin', 'AD'),
    message: 'blocked merchant category',
    detail: 'Gambling',
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(3),
  },
  {
    id: 'pcmact_demo_0004',
    actor: user('Niki Kim', 'NK'),
    message: 'created a virtual card',
    detail: '“Q3 Conference Travel”',
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(4),
  },
  {
    id: 'pcmact_demo_0005',
    actor: extend(),
    message: 'auto-reviewed a transaction',
    detail: null,
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(5),
  },
]

/** Page 2 — exists so "Load more" is exercised against a real second fetch. */
const DEMO_PAGE_2: ActivityEvent[] = [
  {
    id: 'pcmact_demo_0006',
    actor: user('Jordan Avery', 'JA'),
    message: 'froze the card',
    detail: null,
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(8),
  },
  {
    id: 'pcmact_demo_0007',
    actor: user('Admin', 'AD'),
    message: 'lowered the card limit',
    detail: '-$1,500.00 · temporary',
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(11),
  },
  {
    id: 'pcmact_demo_0008',
    actor: user('Priya Raman', 'PR'),
    message: 'requested a limit increase',
    // A FAILED row is seeded on purpose: the design guardrail is that an event
    // reads correctly WITHOUT a tone dot, and this is the row that proves it.
    detail: 'Declined by policy',
    outcome: 'FAILED',
    occurredAt: daysAgo(14),
  },
  {
    id: 'pcmact_demo_0009',
    actor: extend(),
    message: 'restored the card after a temporary hold',
    detail: null,
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(19),
  },
  {
    id: 'pcmact_demo_0010',
    actor: user('Marcus Bell', 'MB'),
    message: 'tried to freeze the card',
    // FAILED with a NULL detail, which the contract explicitly permits. Seeded
    // because it is the row that proves the outcome is legible on its own: with
    // no detail string to lean on, anything short of rendering `outcome` makes
    // this identical to a successful freeze.
    detail: null,
    outcome: 'FAILED',
    occurredAt: daysAgo(22),
  },
]

/** Page 3 — terminal, so the "Load more" control removing itself is exercised. */
const DEMO_PAGE_3: ActivityEvent[] = [
  {
    id: 'pcmact_demo_0011',
    actor: user('Admin', 'AD'),
    message: 'added the card to the Travel budget',
    detail: null,
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(26),
  },
  {
    id: 'pcmact_demo_0012',
    actor: user('Jordan Avery', 'JA'),
    message: 'activated the physical card',
    detail: null,
    outcome: 'SUCCEEDED',
    occurredAt: daysAgo(31),
  },
]

/**
 * Demo stand-in for the server's cursor walk. Keyed by the `before` cursor the
 * caller passes, exactly as the real route is: `undefined` is the first page,
 * and each page names its successor.
 */
export const ACCOUNT_ACTIVITY_DEMO_PAGES: ReadonlyMap<
  string | undefined,
  { items: ActivityEvent[]; nextCursor: string | null }
> = new Map([
  [undefined, { items: DEMO_PAGE_1, nextCursor: DEMO_CURSOR_PAGE_2 }],
  [DEMO_CURSOR_PAGE_2, { items: DEMO_PAGE_2, nextCursor: DEMO_CURSOR_PAGE_3 }],
  [DEMO_CURSOR_PAGE_3, { items: DEMO_PAGE_3, nextCursor: null }],
])
